/**
 * マッチング設定一覧テーブル
 */

import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import { StatusBadge } from "components/ui/StatusBadge";
import { Table } from "components/ui/Table/Table";
import type { MatchingCondition } from "hooks/use-matching-conditions-api";
import type { FC } from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { formatDate } from "utils/relative-time";

type MatchingConditionsTableProps = {
  settings: MatchingCondition[];
  onActivate: (settingId: string) => Promise<void>;
  onDeactivate: (settingId: string) => Promise<void>;
  onDelete: (settingId: string) => Promise<void>;
  isActivating: boolean;
  isDeleting: boolean;
};

const matchingModeLabels: Record<string, string> = {
  manual: "手動",
  review: "レビュー",
  auto: "自動",
};

export const MatchingConditionsTable: FC<MatchingConditionsTableProps> = ({
  settings,
  onActivate,
  onDeactivate,
  onDelete,
  isActivating,
  isDeleting,
}) => {
  const [actionId, setActionId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const handleActivate = async (settingId: string) => {
    setActionId(settingId);
    try {
      await onActivate(settingId);
    } finally {
      setActionId(null);
    }
  };

  const handleDeactivate = async (settingId: string) => {
    setActionId(settingId);
    try {
      await onDeactivate(settingId);
    } finally {
      setActionId(null);
    }
  };

  const handleDelete = async (settingId: string) => {
    setActionId(settingId);
    try {
      await onDelete(settingId);
    } finally {
      setActionId(null);
      setDeleteConfirm(null);
    }
  };

  if (settings.length === 0) {
    return (
      <div className="card bg-base-100 shadow-sm">
        <div className="card-body">
          <p className="text-base-content/60">
            マッチング設定がまだありません。「新規作成」から設定を追加してください。
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        <Table.Root density="compact" zebra stickyHeader>
          <Table.Head sticky>
            <tr>
              <Table.HeaderCell>設定名</Table.HeaderCell>
              <Table.HeaderCell>ステータス</Table.HeaderCell>
              <Table.HeaderCell>マッチングモード</Table.HeaderCell>
              <Table.HeaderCell>最低マージン</Table.HeaderCell>
              <Table.HeaderCell>最低スコア</Table.HeaderCell>
              <Table.HeaderCell>メール有効期間</Table.HeaderCell>
              <Table.HeaderCell>更新日時</Table.HeaderCell>
              <Table.HeaderCell>操作</Table.HeaderCell>
            </tr>
          </Table.Head>
          <Table.Body>
            {settings.map(setting => (
              <Table.Row key={setting.id}>
                <Table.Cell>
                  <div>
                    <div className="font-medium">{setting.name}</div>
                    {setting.description && (
                      <div className="text-base-content/60 truncate max-w-xs">
                        {setting.description}
                      </div>
                    )}
                  </div>
                </Table.Cell>
                <Table.Cell>
                  {setting.isActive ? (
                    <StatusBadge tone="soft-success">アクティブ</StatusBadge>
                  ) : (
                    <StatusBadge tone="soft-neutral">非アクティブ</StatusBadge>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {matchingModeLabels[setting.matchingMode] ||
                    setting.matchingMode}
                </Table.Cell>
                <Table.Cell>{setting.minMargin.toLocaleString()}円</Table.Cell>
                <Table.Cell>
                  {Math.round(setting.minTotalScore * 100)}%
                </Table.Cell>
                <Table.Cell>
                  {setting.maxEmailAgeDays
                    ? `${setting.maxEmailAgeDays}日`
                    : "制限なし"}
                </Table.Cell>
                <Table.Cell>{formatDate(setting.updatedAt)}</Table.Cell>
                <Table.Cell>
                  <div className="flex gap-1">
                    {setting.isActive ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="xs"
                        onClick={() => handleDeactivate(setting.id)}
                        disabled={isActivating && actionId === setting.id}
                      >
                        {isActivating && actionId === setting.id ? (
                          <Loading size="xs" />
                        ) : (
                          "無効化"
                        )}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="ghost"
                        size="xs"
                        onClick={() => handleActivate(setting.id)}
                        disabled={isActivating && actionId === setting.id}
                      >
                        {isActivating && actionId === setting.id ? (
                          <Loading size="xs" />
                        ) : (
                          "有効化"
                        )}
                      </Button>
                    )}
                    <Button asChild variant="ghost" size="xs">
                      <Link
                        to={`/settings/matching/conditions/${setting.id}/edit`}
                      >
                        編集
                      </Link>
                    </Button>
                    {!setting.isActive && (
                      <Button
                        type="button"
                        variant="destructive"
                        size="xs"
                        onClick={() => setDeleteConfirm(setting.id)}
                        disabled={isDeleting && actionId === setting.id}
                      >
                        削除
                      </Button>
                    )}
                  </div>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </div>

      {deleteConfirm && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">設定を削除しますか？</h3>
            <p className="py-4">この操作は取り消せません。</p>
            <div className="modal-action">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDeleteConfirm(null)}
                disabled={isDeleting}
              >
                キャンセル
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => handleDelete(deleteConfirm)}
                disabled={isDeleting}
              >
                {isDeleting && actionId === deleteConfirm ? (
                  <Loading size="sm" />
                ) : (
                  "削除"
                )}
              </Button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => !isDeleting && setDeleteConfirm(null)}
          />
        </div>
      )}
    </>
  );
};
